/**
 * Course category Book/Course Intro CMS service (live editor API).
 */
import api from '../api/index.api'

const nameOf = (translations, locale) =>
	translations?.[locale]?.name ||
	translations?.en?.name ||
	translations?.vi?.name ||
	'—'

const pickLocaleIntro = (translations, locale) => {
	const t = translations?.[locale] || {}
	return {
		introTitle: t.introTitle || '',
		introHighlight: t.introHighlight || '',
		introBody: t.introBody || '',
		introMore: t.introMore || '',
	}
}

const normalizeNode = (node, parent = null) => {
	const translations = node.translations || {}
	return {
		id: Number(node.id),
		parentId: node.parentID ?? node.parentId ?? parent?.id ?? null,
		parentNameEn: parent ? nameOf(parent.translations, 'en') : null,
		isRoot: parent == null,
		translations,
		nameEn: nameOf(translations, 'en'),
		nameVi: nameOf(translations, 'vi'),
		isNew: Boolean(node.isNew),
		introEnabled: Boolean(node.introEnabled),
		coverImageUrl: node.coverImageUrl || '',
		authorName: node.authorName || '',
		authorUserID:
			node.authorUserID != null && node.authorUserID !== ''
				? Number(node.authorUserID)
				: null,
		en: pickLocaleIntro(translations, 'en'),
		vi: pickLocaleIntro(translations, 'vi'),
	}
}

const flattenTree = roots => {
	const rows = []
	for (const root of roots || []) {
		rows.push(normalizeNode(root, null))
		for (const sub of root.subCategories || []) {
			rows.push(normalizeNode(sub, root))
		}
	}
	return rows
}

/**
 * Flat list of roots + leaves for admin table.
 * @param {{ q?: string, introOnly?: boolean }} [opts]
 */
export async function listCategoriesForIntro(opts = {}) {
	const { q = '', introOnly = false } = opts
	const raw = await api.courseCategoryAdminApi.getAll()
	const tree = Array.isArray(raw) ? raw : []
	const allRows = flattenTree(tree)
	const enabledTotal = allRows.filter(r => r.introEnabled).length

	let rows = allRows
	const query = String(q || '').trim().toLowerCase()
	if (query) {
		rows = rows.filter(
			r =>
				r.nameEn.toLowerCase().includes(query) ||
				r.nameVi.toLowerCase().includes(query) ||
				String(r.id).includes(query)
		)
	}
	if (introOnly) {
		rows = rows.filter(r => r.introEnabled)
	}
	rows.sort((a, b) => {
		if (a.introEnabled !== b.introEnabled) return a.introEnabled ? -1 : 1
		return a.id - b.id
	})
	return {
		data: rows,
		total: rows.length,
		enabledCount: enabledTotal,
	}
}

/**
 * Full-replace intro CMS fields for a category.
 */
export async function saveCategoryIntro(id, form) {
	const cover = String(form.coverImageUrl || '').trim()
	const authorName = String(form.authorName || '').trim()
	let authorUserID = form.authorUserID
	if (authorUserID === '' || authorUserID == null) {
		authorUserID = null
	} else {
		authorUserID = Number(authorUserID)
		if (!Number.isFinite(authorUserID) || authorUserID <= 0) {
			throw new Error('Author user ID must be a positive number when set.')
		}
	}
	if (cover && !/^(https?:\/\/|\/)/i.test(cover)) {
		throw new Error('Cover URL must be http(s) or a root-relative path (/...).')
	}

	const payload = {
		introEnabled: Boolean(form.introEnabled),
		coverImageUrl: cover,
		authorName,
		authorUserID,
		translations: [
			{
				language: 'en',
				introTitle: form.en?.introTitle || '',
				introHighlight: form.en?.introHighlight || '',
				introBody: form.en?.introBody || '',
				introMore: form.en?.introMore || '',
			},
			{
				language: 'vi',
				introTitle: form.vi?.introTitle || '',
				introHighlight: form.vi?.introHighlight || '',
				introBody: form.vi?.introBody || '',
				introMore: form.vi?.introMore || '',
			},
		],
	}
	return api.courseCategoryAdminApi.setIntro(id, payload)
}
